import type { i18n } from 'i18next';

/**
 * Notify facade components about language change
 * This ensures facade components update immediately when the frontend language changes
 */
export const notifyFacadeLanguageChange = (language: string) => {
  if (typeof window !== 'undefined') {
    console.log('Frontend: Dispatching facade language change event:', language);
    window.dispatchEvent(
      new CustomEvent('facade-language-change', {
        detail: { language },
      }),
    );
  }
};

/**
 * Mirror the active locale onto <html lang>. index.html ships a static
 * `lang="en"` and there is no SSR, so this is the only thing that tells the
 * browser which hyphenation dictionary to use and a screen reader which voice
 * to read in — for all eleven locales, on every element.
 *
 * Returns an unsubscribe so a test can remove the listener; each call registers
 * a fresh closure, so two calls leave two listeners and two writes per change.
 *
 * Touches `document` unguarded, unlike notifyFacadeLanguageChange above. This is
 * called once from the browser entry, which itself reaches for
 * document.getElementById('root') a few lines on; a typeof guard here would
 * imply an SSR path that the entry rules out.
 */
export const syncDocumentLanguage = (i18n: i18n): (() => void) => {
  const apply = () => {
    // resolvedLanguage, not language: the detector yields region tags, and the
    // fallbackLng map's `no-NB` / `no-NN` are not valid BCP-47 — a screen reader
    // given those has nothing to match. resolvedLanguage is the bundle actually
    // in use, so always one of the eleven. Same reasoning as language-select.tsx:26.
    const lang = i18n.resolvedLanguage ?? i18n.language;
    if (lang) document.documentElement.lang = lang;
  };

  i18n.on('languageChanged', apply);
  apply();

  return () => i18n.off('languageChanged', apply);
};
